/**
 * Status color utility functions for consistent status display across the application
 * Aligned with existing COLORS and LEGEND_ITEMS system
 */

import COLORS from '@utils/color';

export const getStatusColor = (status: string): string => {
  const statusColors: { [key: string]: string } = {
    // Backend enum values (uppercase) - using existing COLORS
    ACTIVE: COLORS.green,
    INACTIVE: COLORS.orange,
    COMPLETE: COLORS.lightBlue,
    REJECTED: COLORS.error,
    CANCELED: COLORS.cancel,
    INTERNAL_APPROVAL: COLORS.darkerGreen,
    CLIENT_APPROVAL: COLORS.darkGreen,
    DELEGATION_CANCELED: COLORS.violet,

    // Legacy formats for backward compatibility
    Active: COLORS.green,
    Inactive: COLORS.orange,
    Complete: COLORS.lightBlue,
    Rejected: COLORS.error,
    Canceled: COLORS.cancel,
    'Internal Approval': COLORS.darkerGreen,
    'Client Approval': COLORS.darkGreen,
    'Delegation Canceled': COLORS.violet,

    // Additional common variations
    UNASSIGNED: COLORS.orange,
    Unassigned: COLORS.orange,
    UPCOMING: COLORS.upcoming,
    Upcoming: COLORS.upcoming,
    BEHIND_SCHEDULE: COLORS.error,
    ASSIGNED_TO_CREW: COLORS.green,
    ASSIGNED_TO_SUBCONTRACTOR: COLORS.darkBlue,
    IN_NEGOTIATIONS: COLORS.negociations,
  };

  return statusColors[status] || COLORS.lightGray; // default gray color
};

/**
 * Get text color that contrasts well with the status background color
 *
 * Story-071 (sprint 14), fix round 1: predates story-071 and both branches always returned
 * white regardless of background — left as-is on purpose (its only consumer,
 * `getTaskStatusConfigs`, is outside this story's surfaces). Do not wire real contrast logic in
 * here; new callers should use `getReadableTextColor()` below instead.
 */
export const getStatusTextColor = (status: string): string => {
  const backgroundColor = getStatusColor(status);

  // Light colors that need dark text
  const lightColors = ['#FA8072', '#3cbde6'];

  if (lightColors.includes(backgroundColor)) {
    return '#ffffff'; // white text
  }

  return '#ffffff'; // default white text for most status colors
};

/**
 * WCAG relative luminance / contrast ratio, per the W3C formula
 * (https://www.w3.org/TR/WCAG21/#dfn-relative-luminance). Shared logic and thresholds with
 * mobile's `getReadableTextColor` in `redesign/src/theme/index.ts` — keep the two in sync.
 */
const hexToRgb = (hex: string): [number, number, number] => {
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean;
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
};

const relativeLuminance = ([r, g, b]: [number, number, number]): number => {
  const [rs, gs, bs] = [r, g, b].map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const contrastRatio = (luminanceA: number, luminanceB: number): number => {
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
};

/** Relative luminance of pure white / pure black — the two endpoints we choose text from. */
const WHITE_LUMINANCE = 1;
const BLACK_LUMINANCE = 0;

/**
 * Story-071 (sprint 14), fix round 1 (Critical #4): picks black or white text for a given
 * palette background hex, whichever has the higher WCAG contrast ratio against it. Used
 * whenever a status color is rendered as a solid background with text on top (e.g. `Badge`'s
 * `color` prop) instead of hardcoding white, which is unreadable on several of this palette's
 * lighter hexes (e.g. `IN_NEGOTIATIONS` `#DAF7A6`).
 */
export const getReadableTextColor = (bgHex: string): string => {
  const bgLuminance = relativeLuminance(hexToRgb(bgHex));
  const contrastWithWhite = contrastRatio(bgLuminance, WHITE_LUMINANCE);
  const contrastWithBlack = contrastRatio(bgLuminance, BLACK_LUMINANCE);
  return contrastWithBlack >= contrastWithWhite ? '#000000' : '#ffffff';
};

/**
 * Format status display name for consistent UI
 */
export const formatStatusDisplayName = (status: string): string => {
  if (!status) return '';

  // Handle uppercase enum format
  if (status === status.toUpperCase()) {
    return status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  // Return as-is for legacy formats
  return status;
};

/**
 * Status configuration for dropdowns and displays
 */
export interface StatusConfig {
  value: string;
  label: string;
  color: string;
  textColor: string;
}

/**
 * Get all available task statuses with their display configuration
 */
export const getTaskStatusConfigs = (): StatusConfig[] => {
  const statuses = [
    'ACTIVE',
    'INACTIVE',
    'COMPLETE',
    'REJECTED',
    'CANCELED',
    'INTERNAL_APPROVAL',
    'CLIENT_APPROVAL',
    'DELEGATION_CANCELED',
  ];

  return statuses.map((status) => ({
    value: status,
    label: formatStatusDisplayName(status),
    color: getStatusColor(status),
    textColor: getStatusTextColor(status),
  }));
};

/**
 * Non-task status color families.
 *
 * Story-071 (sprint 14): web is the canonical source for every status color family, but most
 * of these families are rendered on web via Tailwind utility classes (see
 * `BudgetDetailsModal/components/utils.ts`, `atoms/InvoiceStatusBadge`,
 * `atoms/EquipmentStatusBadge`, `components/IncidentCard`) rather than raw hex constants. These
 * maps exist so this single palette source has one literal, testable hex per status — the exact
 * value each of those components already resolves to — for mobile to mirror. Adding them here is
 * additive only: it does not change what any existing web component renders.
 */

/** Hex equivalents of `atoms/InvoiceStatusBadge`'s Tailwind `solidByStatus` classes. */
export const INVOICE_STATUS_COLORS: { [key: string]: string } = {
  DRAFT: '#64748b', // slate-500
  SENDING: '#3b82f6', // blue-500
  SENT: '#6366f1', // indigo-500
  PAID: '#10b981', // emerald-500
  VOIDED: '#9ca3af', // gray-400
  SEND_FAILED: '#ef4444', // red-500
};

export const getInvoiceStatusColor = (status: string): string =>
  INVOICE_STATUS_COLORS[status] || COLORS.lightGray;

/**
 * Matches `components/IncidentCard`'s MUI `<Chip color={getStatusColor(status)} />`, resolved to
 * this app's (unmodified) MUI default theme: `error.main` / `warning.main` in light mode.
 */
export const INCIDENT_STATUS_COLORS: { [key: string]: string } = {
  PENDING: '#d32f2f', // MUI default error.main
  ACKNOWLEDGED: '#ed6c02', // MUI default warning.main
};

export const getIncidentStatusColor = (status: string): string =>
  INCIDENT_STATUS_COLORS[status] || COLORS.lightGray;

/** Hex equivalents of `atoms/EquipmentStatusBadge`'s Tailwind `dotColor` classes. */
export const EQUIPMENT_STATUS_COLORS: { [key: string]: string } = {
  AVAILABLE: '#22c55e', // green-500
  IN_USE: '#3b82f6', // blue-500
  MAINTENANCE: '#eab308', // yellow-500
  RESERVED: '#a855f7', // purple-500
  RETIRED: '#6b7280', // gray-500
};

export const getEquipmentStatusColor = (status: string): string =>
  EQUIPMENT_STATUS_COLORS[status] || COLORS.lightGray;
